import { useMemo } from 'react';
import { Alert, Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { downloadTextFile } from '../../../lib/download';
import { runOfShowCsv, runOfShowRows } from '../../../lib/hostExport/runOfShowExport';
import type { GroupDetail } from '../../../types/groups';
import PrintableSection from './PrintableSection';

/**
 * The itinerary, in chronological order, times in the trip's own zone — the
 * ops document for the day of, not the guest-facing plan.
 *
 * This shows EVERY item regardless of draft/publish/audience, because a host
 * running the day needs the whole thing. That makes the printed sheet a
 * host-only document, and it says so on the page: a run of show left on a
 * table is otherwise a draft-and-private-plans leak with nothing on it to
 * warn whoever picks it up.
 *
 * Ordering and time rendering come from `runOfShowRows`
 * (lib/hostExport/runOfShowExport.ts), which sorts on the real instant and
 * renders through `formatRangeInZone` — the same formatter the Itinerary tab
 * uses. The zone is printed on the sheet, and when the trip has no zone of
 * its own the fallback (the printing device's zone) is called out, because
 * two hosts printing from two cities would otherwise hold two different
 * sheets with nothing on either to say so.
 */
export default function RunOfShowExportSection({
  group,
  timeZone,
  timeZoneIsFallback,
  printing,
  onPrint,
}: {
  group: GroupDetail;
  timeZone: string;
  timeZoneIsFallback: boolean;
  printing: boolean;
  onPrint: () => void;
}) {
  const items = group.itineraryItems ?? [];
  const rows = useMemo(() => runOfShowRows(items, timeZone), [items, timeZone]);

  return (
    <PrintableSection
      title="Run of show"
      description="The full itinerary, in order, times in the trip's own zone."
      printing={printing}
      onPrint={onPrint}
      onDownloadCsv={() => downloadTextFile(`run-of-show-${group.slug}.csv`, runOfShowCsv(items, timeZone))}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        All times {timeZone}. Host copy — includes drafts and host-only items not shown to guests.
      </Typography>
      {timeZoneIsFallback ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This trip has no time zone set, so times are shown in this device&apos;s zone ({timeZone}).
          Set the trip&apos;s zone on the Overview tab before handing this sheet to anyone.
        </Alert>
      ) : null}
      {rows.length ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">Time</TableCell>
                <TableCell component="th" scope="col">Event</TableCell>
                <TableCell component="th" scope="col">Location</TableCell>
                <TableCell component="th" scope="col">Status</TableCell>
                <TableCell component="th" scope="col">Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                // eslint-disable-next-line react/no-array-index-key -- rows have no stable id of their own
                <TableRow key={index}>
                  <TableCell component="th" scope="row">{row.time}</TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{row.location}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.notes}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Typography color="text.secondary">Nothing on the itinerary yet.</Typography>
      )}
    </PrintableSection>
  );
}
